import { useNavigate } from "react-router-dom";
import { clean, estadoAprobacionCssVar } from "./reportesUtils.ts";
import NumberBoxes, { NumberBoxItem } from "./NumberBoxes.tsx";

interface EstadoConteo {
  codigo: string;
  cantidad: number;
}

export interface EstadoBox {
  codigo: string;
  titulo: string;
  // State name the maintenance screen filters by (EQUALS match)
  estado: string;
}

// Where a click lands, like the legacy NumberBox links
export interface MantencionLink {
  ruta: string; // "solicitudes-compra/mantencion" | "ordenes-compra/mantencion"
  paramEstado: string; // socest_nombre | ocest_nombre
  paramFecha: string; // soc_dt_cre | oc_dt_cre
}

interface EstadosBoxesProps {
  // Approval states as returned by /reports/{solicitudes|ordenes}/estados
  estados: EstadoConteo[] | null;
  // States to show, in order (partial states are left out on purpose)
  boxes: EstadoBox[];
  link: MantencionLink;
  // Applied dashboard range, YYYY-MM-DD
  fechaDesde?: string;
  fechaHasta?: string;
  loading: boolean;
  error: string | null;
  // Heading of the strip, e.g. "Documentos OC" / "Líneas OC"
  titulo?: string;
  nota?: string;
  // false: boxes are not links (line counts: the maintenance screen filters
  // documents by their own state, not by line state)
  navegable?: boolean;
}

/**
 * The maintenance date filter is a "contains" text match on dd-mm-yyyy,
 * so it can only express a month ("-09-2026") or a year ("-2026"), the
 * same values the legacy index links use. Returns the tightest one that
 * covers the dashboard range, or "" (no date filter).
 */
export const filtroFechaMantencion = (desde?: string, hasta?: string) => {
  const d = /^(\d{4})-(\d{2})/.exec(desde ?? "");
  const h = /^(\d{4})-(\d{2})/.exec(hasta ?? "");
  if (!d || !h || d[1] !== h[1]) return "";
  return d[2] === h[2] ? `-${d[2]}-${d[1]}` : `-${d[1]}`;
};

/**
 * "Big numbers" strip per approval state, with the same look, colors and
 * click-through to the maintenance screen as the legacy index NumberBox row.
 */
export default function EstadosBoxes({
  estados,
  boxes,
  link,
  fechaDesde,
  fechaHasta,
  loading,
  error,
  titulo,
  nota,
  navegable = true,
}: EstadosBoxesProps) {
  const navigate = useNavigate();
  const rows = estados ?? [];
  const cantidad = (codigo: string) =>
    rows
      .filter((e) => clean(e.codigo) === codigo)
      .reduce((a, e) => a + e.cantidad, 0);
  // Totales counts every state, partial ones included
  const total = rows.reduce((a, e) => a + e.cantidad, 0);
  const fecha = filtroFechaMantencion(fechaDesde, fechaHasta);

  const open = (estado: string) => {
    const params = new URLSearchParams();
    params.set(link.paramEstado, estado);
    params.set(link.paramFecha, fecha);
    navigate(`/${link.ruta}?${params.toString()}`);
  };

  const hint = (estado: string) =>
    navegable
      ? `Ver ${estado ? `"${estado}"` : "todas"} en mantención${
          fecha ? ` (fecha contiene ${fecha})` : " (sin filtro de fecha)"
        }`
      : undefined;

  const items: NumberBoxItem[] = [
    {
      key: "TOTAL",
      titulo: "Totales",
      color: "var(--primary-app-color)",
      value: total,
      hint: hint(""),
      onClick: navegable ? () => open("") : undefined,
    },
    ...boxes.map((b) => ({
      key: b.codigo,
      titulo: b.titulo,
      color: `var(${estadoAprobacionCssVar(b.codigo) ?? "--primary-app-color"})`,
      value: cantidad(b.codigo),
      hint: hint(b.estado),
      onClick: navegable ? () => open(b.estado) : undefined,
    })),
  ];

  return (
    <NumberBoxes
      items={items}
      loading={loading}
      error={error}
      titulo={titulo}
      nota={nota}
    />
  );
}
